import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '../store/authStore'
import api from '../utils/api'
import logger from '../utils/logger'

const CONSENT_KEY = 'cookie_consent'
const CONSENT_VERSION = '2026.02.05'

const defaultConsent = {
  necessary: true, // Always true, cannot be disabled
  analytics: false,
  marketing: false,
  version: null,
  timestamp: null
}

const CookieConsentContext = createContext(null)

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext)
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider')
  }
  return context
}

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(defaultConsent)
  const [showBanner, setShowBanner] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const user = useAuthStore(state => state.user)

  // Load consent from localStorage on mount, or from backend for authenticated users
  useEffect(() => {
    const loadConsent = async () => {
      const stored = localStorage.getItem(CONSENT_KEY)

      if (stored) {
        try {
          const parsed = JSON.parse(stored)
          // Check if consent version matches current version
          if (parsed.version === CONSENT_VERSION) {
            setConsent(parsed)
            setShowBanner(false)
            return
          }
        } catch {
          // Invalid stored consent
        }
      }

      // If user is authenticated but no local consent, try to load from backend
      if (user) {
        try {
          const response = await api.get('/api/consent/my')
          if (response.data.success && response.data.data) {
            const { analytics, marketing } = response.data.data
            // Sync backend consent to localStorage
            const consentData = {
              necessary: true,
              analytics: analytics?.accepted || false,
              marketing: marketing?.accepted || false,
              version: CONSENT_VERSION,
              timestamp: new Date().toISOString()
            }
            localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData))
            setConsent(consentData)
            setShowBanner(false)
            return
          }
        } catch (error) {
          logger.debug('Failed to load consent from backend:', error)
        }
      }

      // No valid consent - show banner
      setShowBanner(true)
    }

    loadConsent()
  }, [user])

  // Save consent to localStorage and sync with backend
  const saveConsent = useCallback(async (newConsent) => {
    const consentData = {
      ...newConsent,
      necessary: true, // Always true
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString()
    }
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData))
    setConsent(consentData)
    setShowBanner(false)
    setShowSettings(false)

    // Sync with backend if user is authenticated (ФЗ-152: consent must be provable)
    if (user) {
      try {
        await api.put('/api/consent/bulk', {
          analytics: newConsent.analytics,
          marketing: newConsent.marketing
        })
        logger.debug('Consent synced with backend')
      } catch (error) {
        logger.error('Failed to sync consent with backend:', error)
        // Don't throw - local consent is still saved
      }
    }
  }, [user])

  // Accept all cookies
  const acceptAll = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true
    })
  }, [saveConsent])

  // Accept only necessary cookies
  const acceptNecessary = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false
    })
  }, [saveConsent])

  // Update specific consent
  const updateConsent = useCallback((type, value) => {
    if (type === 'necessary') return // Cannot change necessary
    setConsent(prev => ({
      ...prev,
      [type]: value
    }))
  }, [])

  // Save custom settings
  const saveSettings = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: consent.analytics,
      marketing: consent.marketing
    })
  }, [consent.analytics, consent.marketing, saveConsent])

  // Open settings modal
  const openSettings = useCallback(() => {
    setShowSettings(true)
  }, [])

  // Close settings modal
  const closeSettings = useCallback(() => {
    setShowSettings(false)
  }, [])

  // Reopen banner (for "Manage Cookies" button in footer)
  const reopenBanner = useCallback(() => {
    setShowBanner(true)
  }, [])

  // Check if analytics is allowed
  const isAnalyticsAllowed = consent.analytics && consent.version === CONSENT_VERSION

  // Check if marketing is allowed
  const isMarketingAllowed = consent.marketing && consent.version === CONSENT_VERSION

  const value = {
    consent,
    showBanner,
    showSettings,
    acceptAll,
    acceptNecessary,
    updateConsent,
    saveSettings,
    openSettings,
    closeSettings,
    reopenBanner,
    isAnalyticsAllowed,
    isMarketingAllowed
  }

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  )
}

export default CookieConsentContext
